import {
	ReactElement,
	RefObject,
	useCallback,
	useContext,
	useMemo,
	useRef,
	useState
} from "react";
import styles from "./ColorPicker.module.less";
import { BaseColorPicker, ColorContrastRequirements } from "./BaseColorPicker";
import { DropOverPreview } from "../DropOverPreview/DropOverPreview";
import {
	cssColorToHsv,
	HsvColor,
	hsvToHex
} from "client/Misc/colorFunctions";
import { setTrackingId } from "client/Misc/utils";
import classNames from "classnames";
import { UniqueInputIdContext } from "client/Contexts/UniqueInputIdContext";
import { Guid, newGuid } from "client_legacy/ndaw/guid";

export type ColorPickerProps = {
	value: string | nil;
	onChange(value: string | undefined): void;
	contrastRequirements?: ColorContrastRequirements;
	menuTitle: string;
	trackingId: string;
	tooltip?: string;
};

export const ColorPicker = (props: ColorPickerProps): ReactElement => {
	const { menuTitle } = props;

	const { id } = useContext(UniqueInputIdContext);
	const [dropOverId] = useState<Guid>(() => newGuid());
	const previewRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	const focusPreviewHandler = useCallback(
		(): void => {
			previewRef.current
				?.focus();
		},
		[]
	);

	const renderPreview = (
		color: string | undefined,
		isOpen: boolean,
		onClick: () => void
	): ReactElement => {
		return (
			<DropOverPreview
				isOpen={isOpen}
				onClick={onClick}
				id={id}
				ariaControls={dropOverId}
				ariaLabel={menuTitle}
				ariaHaspopup="dialog"
				ref={previewRef}
			>
				<div
					className={styles.preview}
					style={{ backgroundColor: color }}
				/>
			</DropOverPreview>

		);
	};

	return (
		<BaseColorPicker
			{...props}
			dropOverId={dropOverId}
			renderPreview={renderPreview}
			onFocusPreview={focusPreviewHandler}
		/>
	);
};
ColorPicker.displayName = "ColorPicker";

export type ColorPickerReadOnlyProps = {
	value: string | nil;
	trackingId: string;
};

export const ColorPickerReadOnly = (props: ColorPickerReadOnlyProps): ReactElement => {
	const {
		value: valueFromProps,
		trackingId
	} = props;

	const hsvColorFromProp: HsvColor | undefined = useMemo(
		() => cssColorToHsv(valueFromProps),
		[valueFromProps]
	);

	const previewColor: string | undefined = hsvToHex(hsvColorFromProp);

	return (
		<div
			{...setTrackingId(trackingId)}
			className={classNames(
				styles.preview,
				styles.preview_readonly
			)}
			style={{ backgroundColor: previewColor }}
		/>
	);
};
ColorPickerReadOnly.displayName = "ColorPickerReadOnly";
